import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
import {
  Button,
  CustomDatePicker,
  DevNote,
  DragAndDrop,
  Drawer,
  FormFieldSet,
  PageHeader,
  PageTitle,
  showToast,
  TableAdv,
  useFormHandler,
} from "../../../../../components";
import HorizontalScale from "../../../../../components/Common/HorizontalScale";
import { mapTableRows } from "../../../../../utils";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addResumeMining, updateResumeMining } from "../../../../../redux/slices/bdmjobs/bdmjobsApi";
import { searchLocations } from "../../../../../redux/slices/internalDbES/internalDbESApi";
import {
  MonsterEducationLevel,
  careerLevelMonster,
  optionsMonsterTo,
  MonsterRadiusOptions,
  workAuthorizationIntegrationSearch,
  LanguageskillsMonster,
} from "../../../../../assets/data/enumData";

const ScheduleResumeMining = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [keywordString, setKeywordString] = useState("");
  const [excludeKeywords, setExcludeKeywords] = useState([]);
  const [excludeJobTitle, setExcludeJobTitle] = useState([]);
  const [excludePreviousEmployer, setExcludePreviousEmployer] = useState([]);
  const [locationTypingValue, setLocationTypingValue] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);

  const { user } = useSelector((state) => state.auth);
  const { jobId: stateJobId, jobCode } = location.state || {};
  const jobId = stateJobId || sessionStorage.getItem("selectedJobUiid");

  const validationRules = {
    schedule: {
      type: "number",
      required: true,
    },
    date: {
      type: "date",
      required: true,
    },
    limit: {
      type: "text",
      required: true,
      number: true,
    },
    harvestFrom: {
      type: "string",
      required: true,
    },
    interval: {
      type: "string",
      required: true,
    },
    gender: {
      type: "number",
    },
    willingToRelocate: {
      type: "array",
      required: true,
    },
  };

  const [tagJob, setTagJob] = useState(false);

  const initialFormData = {
    schedule: "",
    date: dayjs.utc().startOf("day").toDate(),
    limit: "",
    interval: "00:00",
    harvestFrom: "",
    recentSkills: "",
    skillWiseExperience: "",
    experience: "",
    locationValue: "",
    locationType: { city: "", zip: "", country: "", state: "" },
    distanceRadius: "",
    willingToRelocate: [],
    languagesKnown: [],
    gender: "",
    qualification: "",
    subQualification: "",
    employmentType: "",
    workAuthorization: "",
    candidateStatus: "",
    poolStatus: "",
    workedInCountry: "",
    candidateType: "",
    lastUpdatedOn: "",
    workLocationStatus: [],
    previousEmployer: "",
    currentSalary: "",
    currentSalaryMin: "",
    currentSalaryMax: "",
    expectedSalary: "",
    expectedSalaryMin: "",
    expectedSalaryMax: "",
    careerLevel: "",
    source: [],
    domain: "",
    keywords: "",
    skills: "",
    jobTitle: "",
    excludePreviousEmployer: "",
  };

  const today = useMemo(() => {
    return dayjs.utc().startOf("day").toDate();
  }, []);





  const { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler(initialFormData, validationRules);

  useEffect(() => {
    if (location.state?.task) {
      const task = location.state.task;
      setFormData({
        schedule: task.scheduleType,
        date: task.scheduleDate ? dayjs.utc(task.scheduleDate).toDate() : today,
        limit: task.maximumProfiles,
        interval: task.interval || "00:00",
        harvestFrom: task.source?.sourceName === 2 ? "careerBuilder" : task.source?.sourceName === 1 ? "monster" : "dice",
        recentSkills: task.skills?.[0]?.skillName || "",
        skillWiseExperience: task.skills?.[0]?.skillExperience || "",
        experience: task.skills?.[0]?.totalExperience || "",
        locationValue: task.location?.locationType || "",
        locationType: {
          city: task.location?.city || "",
          zip: task.location?.zip || "",
          country: task.location?.country || "",
          state: task.location?.state || "",
        },
        distanceRadius: task.location?.distanceRadius || "",
        willingToRelocate: task.candidateDetails?.relocateType ? [task.candidateDetails.relocateType] : [],
        languagesKnown: task.candidateDetails?.languagesKnown || [],
        gender: task.candidateDetails?.gender || "",
        qualification: task.candidateDetails?.qualification || "",
        lastUpdatedOn: task.candidatePreference?.lastUpdatedDate || "",
        previousEmployer: task.candidatePreference?.previousEmployer || "",
        expectedSalary: task.candidatePreference?.expectedSalary?.currency || 1,
        expectedSalaryMin: task.candidatePreference?.expectedSalary?.amount?.min || "",
        expectedSalaryMax: task.candidatePreference?.expectedSalary?.amount?.max || "",
        keywords: task.exclude?.keywords?.join(", ") || "",
        skills: task.exclude?.skills?.join(", ") || "",
        jobTitle: task.excludeJobTitle?.join(", ") || "",
        excludePreviousEmployer: task.exclude?.previousEmployer?.join(", ") || "",
      });

      setExcludeJobTitle(task.excludeJobTitle || []);

      // Helper to parse merged boolean string back to include and exclude components
      const parseBooleanString = (fullString) => {
        if (!fullString) return { include: "", excludeList: [] };

        // Split by " AND (NOT " which marks the start of an exclude group
        const parts = fullString.split(" AND (NOT ");
        const include = parts[0] ? parts[0].trim() : "";

        const excludeList = parts.slice(1).map(part => {
          // Remove trailing ")" -> "term)" -> "term"
          let cleanPart = part.replace(/\)$/, "");
          // Handle distributed NOT: "social AND NOT media" -> "social media"
          if (cleanPart.includes(" AND NOT ")) {
            return cleanPart.split(" AND NOT ").join(" ");
          }
          return cleanPart;
        });

        return { include, excludeList };
      };

      // Parse Keywords
      const { include: kwInc, excludeList: kwExc } = parseBooleanString(task.keywords);
      setKeywordString(kwInc);
      setExcludeKeywords(kwExc);

      // Parse Previous Employer
      const { include: empInc, excludeList: empExc } = parseBooleanString(task.candidatePreference?.previousEmployer);
      setExcludePreviousEmployer(empExc);

      // Update formData with parsed strings (both include and exclude)
      setFormData(prev => ({
        ...prev,
        previousEmployer: empInc,
        excludePreviousEmployer: empExc.join(", "),
        keywords: kwExc.join(", ")
      }));

    }
  }, [location.state?.task, setFormData, today]);

  // Fetch location suggestions as user types in City/Zip
  useEffect(() => {
    if (!locationTypingValue || locationTypingValue.length < 2) {
      setLocationSuggestions([]);
      return;
    }
    const [city, state] = (locationTypingValue ?? "").split(",");
    if (city && state) return;

    const delay = setTimeout(async () => {
      setLocationSuggestions([]);
      const response = await searchLocations({
        locationValue: locationTypingValue,
      });
      if (response?.success) {
        const locationData = (response?.data?.data || []).flatMap((item) => {
          const name = item.name ?? "";
          const stateName = item.stateName ?? "";
          if (Array.isArray(item.zipCode)) {
            return item.zipCode.map((zc) => `${name},${stateName},${zc}`);
          }
          return [`${name},${stateName},${item.zipCode ?? ""}`];
        });
        const search = locationTypingValue.toLowerCase();
        const startsWith = locationData.filter((item) =>
          item.toLowerCase().startsWith(search)
        );
        const contains = locationData.filter(
          (item) =>
            item.toLowerCase().includes(search) &&
            !item.toLowerCase().startsWith(search)
        );
        startsWith.sort((a, b) => a.localeCompare(b));
        contains.sort((a, b) => a.localeCompare(b));
        setLocationSuggestions([...startsWith, ...contains]);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [locationTypingValue]);

  const columns = [
    {
      key: "jobCode",
      label: "Job Code",
      render: (row) => <div className="text-info-500">{row.jobCode}</div>,
    },
    {
      key: "jobTitle",
      label: "Job Title",
    },
    {
      key: "location",
      label: "Location",
    },
    {
      key: "client",
      label: "Client",
    },
  ];
  // console.log("Formdata==>", formData.date)
  const rows = Array.from({ length: 6 }, (_, i) => ({
    id: i,
    jobCode: "OOJ-1000",
    jobTitle: "Project Manager",
    location: "Florida,USA",
    client: "Internal",
  }));
  const mappedRows = mapTableRows(rows, columns);

  const handleSave = async () => {
    const isValid = validateAllFields();

    if (isValid) {
      let utcInterval = "";
      if (formData.date && formData.interval) {
        const dateStr = dayjs(formData.date).format("YYYY-MM-DD");

        // Parse the input as UTC directly (not local)
        const selectedUTC = dayjs.utc(`${dateStr}T${formData.interval}:00`);
        const nowUTC = dayjs.utc();
        const diffInMinutes = selectedUTC.diff(nowUTC, "minute");

        if (!selectedUTC.isValid()) {
          showToast("Invalid date or time selected", "error");
          return;
        }

        // Must be STRICTLY greater than 30 minutes in the future
        if (diffInMinutes <= 30) {
          showToast("you can only schedule after 30 min of current time", "error");
          return;
        }

        utcInterval = selectedUTC.format("HH:mm");
      }

      const payload = {
        scheduleType: Number(formData.schedule),
        scheduleDate: dayjs.utc(dayjs(formData.date).format("YYYY-MM-DD")).toDate(),
        maximumProfiles: Number(formData.limit),
        interval: utcInterval, // Sending UTC string instead of Number
        keywords: buildBooleanKeywords(keywordString, excludeKeywords),
        source: formData.harvestFrom === "careerBuilder" ? 2 : formData.harvestFrom === "monster" ? 1 : 2, // default to careerBuilder if not set,
        location: {
          locationType: Number(formData.locationValue),
          city: formData.locationType.city,
          zip: formData.locationType.zip,
          country: formData.locationType.country,
          state: formData.locationType.state,
          distanceRadius: Number(formData.distanceRadius) || 0,
        },
        candidateDetails: {
          relocateType: Number(formData.willingToRelocate?.[0] || formData.willingToRelocate) || undefined,
          languagesKnown: formData.languagesKnown,
          gender: Number(formData.gender) || undefined,
          qualification: formData.qualification,
        },
        candidatePreference: {
          lastUpdatedDate: Number(formData.lastUpdatedOn) || undefined,
          previousEmployer: buildBooleanKeywords(formData.previousEmployer, excludePreviousEmployer),
          expectedSalary: {
            currency: Number(formData.expectedSalary) || 1,
            amount: {
              min: Number(formData.expectedSalaryMin) || 0,
              max: Number(formData.expectedSalaryMax) || 0
            },
          },
          jobStatus: formData.jobStatus || [],
        },
        excludeJobTitle: excludeJobTitle,
        jobPostId: Number(jobId) || undefined,
        addedBy: Number(user?.data?.uiid) || 0,
        status: 1,
      };


      let res;
      if (location.state?.task?.uiid) {
        res = await updateResumeMining({
          uiid: location.state.task.uiid,
          payload: [
            {
              type: "SET",
              value: payload,
            },
          ],
        });
      } else {
        // Create Mode
        res = await addResumeMining({
          payload,
        });
      }

      if (res.success) {
        showToast(location.state?.task?.uiid ? "Resume Mining updated successfully" : "Resume Mining scheduled successfully", "success");
        if (user?.data?.role?.type == 4) {
          navigate("/ats/recruitment/jobs");
        } else {
          navigate(-1);
        }
      } else {
        showToast(res.message || "Failed to save resume mining task", "error");
      }
    } else {
      showToast("Please fill all required fields", "error");
    }
  };

  // const formatDragAndDrop = (keywordArray) => {
  //   if (!Array.isArray(keywordArray) || keywordArray.length === 0) {
  //     setKeywordString("");
  //     return;
  //   }

  //   setKeywordString(
  //     keywordArray
  //       .map((k, i) => `${i ? (k.connector || "or") + " " : ""}${k.keyword}`)
  //       .join(" "),
  //   );
  // };

  // Build boolean keyword string with include and exclude terms
  // Follows the same pattern as buildCBBooleanSearch in IntegratedSearch
  const buildBooleanKeywords = (include = "", exclude = []) => {
    // const normalize = (str = "") =>
    //   str
    //     .replace(/[\u201C\u201D]/g, '"')
    //     .replace(/[\u2018\u2019]/g, "'")
    //     .trim()
    //     .replace(/\s+/g, " ")
    //     .replace(/\bAND\b/gi, "AND")
    //     .replace(/\bOR\b/gi, "OR")
    //     .replace(/\bNOT\b/gi, "NOT");

    // const isOperator = (t) => ["AND", "OR", "NOT"].includes(t);
    // const isParen = (t) => t === "(" || t === ")";
    // const isTerm = (t) => !isOperator(t) && !isParen(t);

    // const tokenize = (str) =>
    //   str.match(/"[^"]*"|\(|\)|\bAND\b|\bOR\b|\bNOT\b|[^\s()]+/gi) || [];

    // const insertMissingOR = (tokens) => {
    //   const out = [];
    //   for (let i = 0; i < tokens.length; i++) {
    //     const curr = tokens[i];
    //     const next = tokens[i + 1];
    //     out.push(curr);
    //     if (!next) continue;
    //     if (isTerm(curr) && isTerm(next)) { out.push("OR"); }
    //     else if (curr === ")" && isTerm(next)) { out.push("OR"); }
    //     else if (isTerm(curr) && next === "(") { out.push("OR"); }
    //     else if (curr === ")" && next === "(") { out.push("OR"); }
    //   }
    //   return out;
    // };

    // Process exclude keywords - each term gets (NOT term) format, split by space if needed
    const excList = Array.isArray(exclude) ? exclude : [];
    const excTerms = excList.filter(Boolean).map((t) => {
      // Split by space to handle "social media" -> (NOT social AND NOT media)
      const tokens = t.trim().split(/\s+/);
      if (tokens.length > 1) {
        return `(${tokens.map(token => `NOT ${token}`).join(" AND ")})`;
      }
      return `(NOT ${t.trim()})`;
    });

    const parts = [];
    if (include && include.trim()) parts.push(include.trim());
    if (excTerms.length) parts.push(excTerms.join(" AND "));

    return parts.join(" AND ");
  };

  const formatStringUsingCommaSeperator = (word, state) => {
    const splitByComma = Array.isArray(word) ? word : (word || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (state === "languagesKnown") {
      setLanguagesKnown(splitByComma);
    } else if (state === "excludeKeywords") {
      setExcludeKeywords(splitByComma);
    } else if (state === "excludeJobTitle") {
      setExcludeJobTitle(splitByComma);
    } else if (state === "excludePreviousEmployer") {
      setExcludePreviousEmployer(splitByComma);
    }
  };

  const hours24Options = Array.from({ length: 24 }, (_, i) => ({
    label: i.toString().padStart(2, "0"),
    value: i.toString().padStart(2, "0"),
  }));

  const minutesOptions = Array.from({ length: 60 }, (_, i) => ({
    label: i.toString().padStart(2, "0"),
    value: i.toString().padStart(2, "0"),
  }));

  const handleTimeComponentChange = (type, val) => {
    const [h, m] = (formData.interval || "00:00").split(":");
    const newInterval = type === "hour" ? `${val}:${m || "00"}` : `${h || "00"}:${val}`;

    // Immediate validation feedback
    if (formData.date && newInterval) {
      const dateStr = dayjs(formData.date).format("YYYY-MM-DD");
      const selectedUTC = dayjs.utc(`${dateStr}T${newInterval}:00`);
      const nowUTC = dayjs.utc();
      const diffInMinutes = selectedUTC.diff(nowUTC, "minute");

      if (diffInMinutes <= 30) {
        showToast("you can only schedule after 30 min of current time", "error");
      }
    }

    handleChange("interval", newInterval);
  };

  const [selectedHour, selectedMinute] = (formData.interval || "00:00").split(":");


  // console.log("FormData: ", formData);


  return (
    <div className="flex flex-col gap-5 flex-1">
      <Button
        title="Schedule Resume Mining"
        color="black"
        variant="text"
        size="md"
        fromLeft
        icon="arrow_left_alt"
        className="self-start text-md font-semibold"
        onClick={() => {
          user?.data?.role?.type == 4
            ? navigate("/ats/recruitment/jobs")
            : navigate(-1);
        }}
      />
      <PageHeader
        Section={
          <div className="flex items-center gap-4 p-2">
            <div className="flex flex-col items-start">
              <PageTitle title={jobCode ? `Harvesting for ${jobCode}` : "Schedule Resume Mining"} tag="h6" />
            </div>
          </div>
        }
        RightSection={
          <Button title={"Tag Job"} onClick={() => setTagJob(true)} />
        }
      />
      <div className="p-2 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {
          <FormFieldSet
            title="Schedule"
            type="select"
            placeholder="Select"
            options={[
              { label: "On selected Date", value: 1 },
              { label: "Till selected Date", value: 2 },
              { label: "Continue until disabled", value: 3 },
            ]}
            value={formData.schedule}
            onChange={(value) => handleChange("schedule", value)}
            error={errors.schedule}
          />
        }
        {formData.schedule !== 3 && (
          <FormFieldSet
            title="Date"
            type="datepicker"
            required
            placeholder="Choose Date"
            value={formData.date}
            minDate={today}
            onChange={(value) => {
              handleChange("date", value);

              // Immediate validation check when date changes
              if (value && formData.interval) {
                const dateStr = dayjs(value).format("YYYY-MM-DD");
                const selectedUTC = dayjs.utc(`${dateStr}T${formData.interval}:00`);
                const diffInMinutes = selectedUTC.diff(dayjs.utc(), "minute");

                if (diffInMinutes <= 30) {
                  showToast("you can only schedule after 30 min of current time", "error");
                }
              }
            }}
            error={errors.date}
          />
        )}
        <FormFieldSet
          title="Limit(Maximum Profiles)"
          type="number"
          placeholder="Limit(Maximum Profiles)"
          value={formData.limit}
          onChange={(value) => handleChange("limit", value)}
          error={errors.limit}
        />
        {/* <FormFieldSet
            title="Interval"
            type="select"
            placeholder="Select"
            value={formData.interval}
            onChange={(value) => handleChange("interval", value)}
            error={errors.interval}
          /> */}
        <div className="flex flex-col">
          <div className="flex justify-between items-center mb-0.5 min-h-[18px]">
            <div className="text-[12px] font-medium">
              <label>Interval (24h) <span className="text-red-600">*</span></label>
            </div>
          </div>
          <div className="min-h-[39px] flex items-center gap-2">
            <div className="w-20">
              <FormFieldSet
                type="select"
                noTitle={true}
                placeholder="HH"
                value={selectedHour}
                options={hours24Options}
                onChange={(val) => handleTimeComponentChange("hour", val)}
              />
            </div>
            <span className="font-bold">:</span>
            <div className="w-20">
              <FormFieldSet
                type="select"
                noTitle={true}
                placeholder="MM"
                value={selectedMinute}
                options={minutesOptions}
                onChange={(val) => handleTimeComponentChange("minute", val)}
              />
            </div>
          </div>
          <div>
            {errors.interval && <span className="text-danger-300 text-xs">{errors.interval}</span>}
          </div>
        </div>
      </div>
      <div className="p-2 flex flex-col gap-3">
        <div className="inner-section rounded p-2">
          <h6>Harvest From</h6>
          <FormFieldSet
            noTitle
            type="radio"
            options={[
              { label: "Career Builder", value: "careerBuilder" },
              { label: "Monster", value: "monster" },
              // { label: "Dice", value: "dice" },
            ]}
            value={formData.harvestFrom}
            onChange={(event) =>
              handleChange("harvestFrom", event.target.value)
            }
            error={errors.harvestFrom}
          />
          {/* <DragAndDrop
            onChange={(keywords, connectors, keyArray) =>
              formatDragAndDrop(keyArray)
            }
          /> */}
        </div>
        <div className="p-2 rounded inner-section">
          {/* <h6>Skills</h6> */}
          <h6>Keywords</h6>
          {/* 
        
          <div className="grid grid-cols-12 gap-x-4 my-4 items-end">
            <div className="col-span-12 md:col-span-10 xl:col-span-11">
              <FormFieldSet
                title="Recent Skills"
                placeholder="Recent Skills"
                value={formData.recentSkills}
                onChange={(value) => handleChange("recentSkills", value)}
                error={errors.recentSkills}
              />
            </div>

            <div className="col-span-12 md:col-span-2 xl:col-span-1 flex justify-end">
              <Button variant="outlined" title="Add" />
            </div>
          </div>

    
          <div className="grid grid-cols-12 gap-x-4 my-4 items-end">
            <div className="col-span-12 md:col-span-10 xl:col-span-11">
              <FormFieldSet
                title="Skill Wise Experience"
                placeholder="Skill Wise Experience"
                value={formData.skillWiseExperience}
                onChange={(value) => handleChange("skillWiseExperience", value)}
                error={errors.skillWiseExperience}
              />
            </div>

            <div className="col-span-12 md:col-span-2 xl:col-span-1 flex justify-end">
              <Button variant="outlined" title="Add" />
            </div>
          </div>

         
          <div className="grid grid-cols-12 gap-x-4 my-4 items-end">
            <div className="col-span-12 md:col-span-10 xl:col-span-11">
              <FormFieldSet
                title=""
                type="select"
                placeholder="Select Experience"
                options={Array.from({ length: 21 }, (_, i) => ({ label: i + " Years", value: i }))}
                value={formData.experience}
                onChange={(value) => handleChange("experience", value)}
                error={errors.experience}
              />
            </div>

            <div className="col-span-12 md:col-span-2 xl:col-span-1 flex justify-end">
              <Button variant="outlined" title={"Add"} />
            </div>
          </div> */}

          <div className="grid grid-cols-12 gap-x-4 my-4 items-end">
            <div className="col-span-12">
              <FormFieldSet
                title="Enter Keywords"
                placeholder="Enter Keywords"
                value={keywordString}
                onChange={(value) => setKeywordString(value)}
                error={errors.keywords} // using errors.keywords since we bind validation there likely
              />
            </div>
          </div>
        </div>

        <div className="p-2 inner-section rounded">
          <div className="flex justify-between items-center ">
            <FormFieldSet
              type="radio"
              title="Location"
              options={[
                { label: "City", value: 1 },
                { label: "Zip", value: 2 },
                { label: "Country/State", value: 3 },
              ]}
              value={formData.locationValue}
              onChange={(event) =>
                handleChange("locationValue", event.target.value)
              }
              error={errors.locationValue}
            />
            <div className="flex items-center">
              {(!formData.locationValue || formData.locationValue == 1) && (
                <FormFieldSet
                  noTitle
                  placeholder="Search City"
                  value={locationTypingValue || formData.locationType.city}
                  onChange={(value) => {
                    setLocationTypingValue(value);
                    setLocationSuggestions([]);
                    handleChange("locationType", {
                      ...formData.locationType,
                      city: value,
                    });
                  }}
                  suggestionsList={locationSuggestions}
                />
              )}
              {formData.locationValue == 2 && (
                <FormFieldSet
                  noTitle
                  placeholder="Search Zip"
                  value={locationTypingValue || formData.locationType.zip}
                  onChange={(value) => {
                    setLocationTypingValue(value);
                    setLocationSuggestions([]);
                    handleChange("locationType", {
                      ...formData.locationType,
                      zip: value,
                    });
                  }}
                  suggestionsList={locationSuggestions}
                />
              )}
              {formData.locationValue == 3 && (
                <div className="flex gap-2">
                  <FormFieldSet
                    noTitle
                    type="countries"
                    placeholder="Select Country"
                    value={formData.locationType.country}
                    onChange={(value) =>
                      handleChange("locationType", {
                        ...formData.locationType,
                        country: value?.value || value,
                        state: "",
                      })
                    }
                  />
                  <FormFieldSet
                    noTitle
                    type="states"
                    placeholder="Select State"
                    parentValue={{ country: formData.locationType.country }}
                    value={formData.locationType.state}
                    onChange={(value) =>
                      handleChange("locationType", {
                        ...formData.locationType,
                        state: value?.value || value,
                      })
                    }
                    disabled={!formData.locationType.country}
                    showSearch={true}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="my-2">
            <HorizontalScale
              onChange={(value) => handleChange("distanceRadius", value)}
            />
          </div>
        </div>
        <div className="px-2 py-4 inner-section rounded grid grid-cols-4 gap-4">
          <h6>Candidate Details</h6>
          <div className="col-span-4">
            {/* <FormFieldSet
              type="select"
              title="Willing to Relocate"
              placeholder="Select"
              options={[
                { label: "Yes", value: 1 },
                { label: "No", value: 2 }
                // { label: "Only Recommended Candidates", value: 2 },
              ]}
              value={formData.willingToRelocate}
              onChange={(value) => handleChange("willingToRelocate", value)}
              error={errors.willingToRelocate}
              showSelectAll={false}
            /> */}


          </div>
          <FormFieldSet
            title="Languages Known"
            type="select"
            options={LanguageskillsMonster}
            placeholder="Languages Known"
            value={formData.languagesKnown}
            onChange={(value) => {
              handleChange("languagesKnown", value);
            }}
            error={errors.languagesKnown}
            multiple={true}
          />
          {/* <FormFieldSet
            title="Gender"
            placeholder="Select"
            type="select"
            options={[
              { label: "Male", value: 1 },
              { label: "Female", value: 2 },
              { label: "Others", value: 3 },
            ]}
            value={formData.gender}
            onChange={(value) => handleChange("gender", value)}
            error={errors.gender}
          /> */}
          <FormFieldSet
            title="Qualification"
            placeholder="Select"
            type="select"
            options={MonsterEducationLevel}
            value={formData.qualification}
            onChange={(value) => handleChange("qualification", value)}
            error={errors.qualification}
          />
          {/* Work Authorization - commented out */}
          {/* <FormFieldSet
            title="Work Authorization"
            placeholder="Select"
            type="select"
            options={workAuthorizationIntegrationSearch}
            value={formData.workAuthorization}
            onChange={(value) => handleChange("workAuthorization", value)}
            error={errors.workAuthorization}
            showSearch={true}
          /> */}
          {/* Worked in (Country) - commented out */}
          {/* <FormFieldSet
            title="Worked in (Country)"
            placeholder="Select"
            type="countries"
            value={formData.workedInCountry}
            onChange={(value) =>
              handleChange("workedInCountry", value?.value || value)
            }
            error={errors.workedInCountry}
          /> */}
        </div>
        <div className="px-2 py-4 inner-section rounded">
          <h6 className="my-1">Candidate Preference</h6>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-4 my-2">
            <FormFieldSet
              title="Resume Posted Within"
              type="select"
              placeholder="Select"
              options={optionsMonsterTo}
              value={formData.lastUpdatedOn}
              onChange={(value) => handleChange("lastUpdatedOn", value)}
              error={errors.lastUpdatedOn}
            />
            {/* Work Location Status - commented out */}
            {/* <FormFieldSet
              title="Work Location Status"
              type="multiSelect"
              placeholder="Select"
              options={[
                { label: "Remote", value: 1 },
                { label: "On Site", value: 2 },
                { label: "Hybrid", value: 3 },
                { label: "Any", value: 4 },
              ]}
              value={formData.workLocationStatus}
              onChange={(value) => handleChange("workLocationStatus", value)}
              error={errors.workLocationStatus}
            /> */}
            <FormFieldSet
              title="Previous Employer"
              type="text"
              placeholder="Enter Previous Employer"
              value={formData.previousEmployer}
              onChange={(value) => handleChange("previousEmployer", value)}
              error={errors.previousEmployer}
            />
          </div>
          {/* Current Salary - commented out */}
          {/* <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-4 my-2">
            <div className="grid grid-cols-3 gap-4">
              <FormFieldSet
                title="Current Salary"
                placeholder="Min"
                value={formData.currentSalaryMin}
                onChange={(value) => handleChange("currentSalaryMin", value)}
                error={errors.currentSalaryMin}
              />
              <FormFieldSet
                title=""
                placeholder="Max"
                value={formData.currentSalaryMax}
                onChange={(value) => handleChange("currentSalaryMax", value)}
                error={errors.currentSalaryMax}
              />
              <FormFieldSet
                title=""
                type="select"
                placeholder="Select"
                options={[
                  { label: "USD", value: 1 },
                  { label: "INR", value: 2 },
                  { label: "EUR", value: 3 },
                  { label: "GBP", value: 4 },
                ]}
                value={formData.currentSalary}
                onChange={(value) => handleChange("currentSalary", value)}
                error={errors.currentSalary}
              />
            </div>
          </div> */}
          <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-4 my-2">
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-4 my-2">
              <FormFieldSet
                title="Expected Salary"
                placeholder="Min"
                value={formData.expectedSalaryMin}
                onChange={(value) => handleChange("expectedSalaryMin", value)}
                error={errors.expectedSalaryMin}
              />
              <FormFieldSet
                title=""
                placeholder="Max"
                value={formData.expectedSalaryMax}
                onChange={(value) => handleChange("expectedSalaryMax", value)}
                error={errors.expectedSalaryMax}
              />
              {/* <FormFieldSet
                title=""
                type="select"
                placeholder="Select"
                options={[
                  { label: "USD", value: 1 },
                  { label: "INR", value: 2 },
                  { label: "EUR", value: 3 },
                  { label: "GBP", value: 4 },
                ]}
                value={formData.expectedSalary}
                onChange={(value) => handleChange("expectedSalary", value)}
                error={errors.expectedSalary}
              /> */}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 my-2">
            {/* Career Level - commented out */}
            {/* <FormFieldSet
              type="select"
              title="Career Level"
              placeholder="Select Career Level"
              options={careerLevelMonster}
              value={formData.careerLevel}
              onChange={(value) =>
                handleChange("careerLevel", value)
              }
              error={errors.careerLevel}
            /> */}
            <FormFieldSet
              type="checkbox"
              size="md"
              title="Job Status"
              multiple={true}
              options={[
                { label: "Full-Time", value: "Full-Time" },
                { label: "Part-Time", value: "Part-Time" },
                { label: "Contract", value: "Contract" },
              ]}
              values={formData.jobStatus}
              onChange={(value) => handleChange("jobStatus", value)}
              error={errors.jobStatus}
            />
            {/* Source - commented out */}
            {/* <FormFieldSet
              type="multiSelect"
              title="Source"
              placeholder="Select"
              options={[
                { label: "Facebook", value: 1 },
                { label: "Twitter", value: 2 },
                { label: "Linkedin", value: 3 },
                { label: "Monster", value: 4 },
                { label: "Direct Sourcing", value: 5 },
                { label: "Career Portal", value: 6 },
                { label: "Indeed", value: 7 },
              ]}
              value={formData.source}
              onChange={(value) => handleChange("source", value)}
              error={errors.source}
            /> */}
          </div>

          {/* <FormFieldSet
              type="select"
              title="Domain"
              placeholder="Select"
              value={formData.domain}
              onChange={(value) => handleChange("domain", value)}
              error={errors.domain}
            /> */}
        </div>
        <div className="flex flex-col inner-section px-2 py-4 rounded">
          <h6>Exclude</h6>
          <div className=" grid grid-cols-4 gap-4 my-2">
            <FormFieldSet
              title="Keywords"
              placeholder="Keywords"
              value={formData.keywords}
              onChange={(value) => {
                handleChange("keywords", value);
                formatStringUsingCommaSeperator(value, "excludeKeywords");
              }}
              error={errors.keywords}
            />
            {/* <FormFieldSet
              title="Skills"
              placeholder="Skills"
              value={formData.skills}
              onChange={(value) => {
                handleChange("skills", value);
                formatStringUsingCommaSeperator(value, "excludeSkills");
              }}
              error={errors.skills}
            /> */}
            <FormFieldSet
              title="Job Title"
              placeholder="Job Title"
              value={formData.jobTitle}
              onChange={(value) => {
                handleChange("jobTitle", value);
                formatStringUsingCommaSeperator(value, "excludeJobTitle");
              }}
              error={errors.jobTitle}
            />
            <FormFieldSet
              title="Previous Employer"
              placeholder="Previous Employer"
              value={formData.excludePreviousEmployer}
              onChange={(value) => {
                handleChange("excludePreviousEmployer", value);
                formatStringUsingCommaSeperator(
                  value,
                  "excludePreviousEmployer",
                );
              }}
              error={errors.excludePreviousEmployer}
            />
          </div>
        </div>
        <div className="flex justify-end items-center gap-4">
          <Button variant="outlined" title={"Cancel"} onClick={() => {
            user?.data?.role?.type === 4
              ? navigate("/ats/recruitment/jobs")
              : navigate(-1);
          }} />
          <Button title={"Save"} onClick={() => handleSave()} />
        </div>
      </div>

      <Drawer
        isOpen={tagJob}
        onClose={() => setTagJob(false)}
        width="w-5xl lg:w-3xl"
        title="Select Job to Share"
        titleColor={"primary-500"}
      >
        <TableAdv
          tableId={"jobs_jobdetails_scheduledresumemining_table"}
          rows={mappedRows}
          columns={columns}
          tdClassName="!py-3"
          toolbarRight={{
            search: true,
            fullScreen: true,
            filter: true,
            download: false,
          }}
        />
      </Drawer>
    </div>
  );
};

export default ScheduleResumeMining;
